// src/components/AnnouncementBanner.js
// Slim announcement banner fixed to the very top of the page.
// Rotates between multiple messages with no gaps.
// Sets --banner-height CSS variable so the header always sits
// exactly below it regardless of device or font size.

import React, { useEffect, useRef, useState, useMemo } from 'react';
import { getMarketDates, upcoming, formatDateRange } from '../data/locations';

const BASE_MESSAGES = [
    'Free Standard Shipping on All Orders',
    'Free Returns Within 14 Days',
];

const AnnouncementBanner = () => {
    const bannerRef = useRef(null);
    // Next confirmed upcoming market, fetched live from Shopify — updates on
    // its own whenever a date is added/confirmed in admin, no deploy needed.
    const [marketMessage, setMarketMessage] = useState(null);

    useEffect(() => {
        getMarketDates()
            .then(all => {
                const next = upcoming(all).find(d => d.confirmed);
                if (next) {
                    setMarketMessage(`Find Us: ${next.city}, ${formatDateRange(next.startDate, next.endDate)}`);
                }
            })
            .catch(() => {});
    }, []);

    const messages = useMemo(
        () => marketMessage ? [...BASE_MESSAGES, marketMessage] : BASE_MESSAGES,
        [marketMessage]
    );

    // Repeat the message list many times to ensure no gap on any screen width
    const items = useMemo(() => Array(15).fill(messages).flat(), [messages]);

    useEffect(() => {
        const updateHeight = () => {
            if (bannerRef.current) {
                const height = bannerRef.current.offsetHeight;
                document.documentElement.style.setProperty('--banner-height', `${height}px`);
            }
        };

        updateHeight();
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, [items]);

    return (
        <div
            ref={bannerRef}
            className="fixed top-0 left-0 right-0 z-[51] bg-[#CEAC8F] text-black text-[10px] tracking-widest uppercase py-2.5 font-medium overflow-hidden whitespace-nowrap"
            style={{ contain: 'layout paint', width: '100vw' }}
        >
            {/* Two identical tracks ensure seamless looping with no blank gap */}
            <div className="flex" style={{ animation: 'marquee 15s linear infinite' }}>
                {items.map((msg, i) => (
                    <span key={`a-${i}`} className="flex-shrink-0 px-12">
                        {msg}
                        <span className="mx-6">✦</span>
                    </span>
                ))}
                {items.map((msg, i) => (
                    <span key={`b-${i}`} className="flex-shrink-0 px-12">
                        {msg}
                        <span className="mx-6">✦</span>
                    </span>
                ))}
            </div>

            <style>{`
                @keyframes marquee {
                    0%   { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                }
            `}</style>
        </div>
    );
};

export default AnnouncementBanner;